// Ask this diagram — the diagram answering for itself.
//
// A reader types words beside a drawing and the elements answer: the ones
// that match light up, the rest dim. Everything here is arithmetic over what
// the file itself carries, read through `elementProperties` - nothing is
// fetched, nothing leaves the browser, and there is no model behind it.
//
// The one rule the whole module is written to: **nothing in here knows which
// diagram it is reading.** There is no list of element types, no notation
// word, no customer term and no example prompt. The suggestions a reader is
// offered are derived from the model in front of them:
//
//   * the kinds it actually contains, in the words the file's own types make;
//   * the values it repeats - a label that appears on many elements with few
//     distinct values is how any file writes "these belong together", whether
//     the tool that wrote it called that a lane, a pool or something nobody
//     here has heard of;
//   * the words that recur in what somebody wrote, by frequency.
//
// A model nobody has seen therefore offers its own suggestions, and a model
// written in another language is read by the same code.
//
// What it will not do is pretend. A question ("which steps have no human in
// the loop") is not a set of search terms, and answering it needs the
// assistant - so the answer says that plainly instead of guessing. That is
// the seam layer 2 arrives at: same panel, same result shape, a different
// source for `answer` and `ids`.

// ── Reading words ────────────────────────────────────────────────────────

// Accents and case are how a word is written, not which word it is: a reader
// typing "prufung" should find "Prüfung", and one typing "AI" should find
// "ai". German's sharp s is the one letter Unicode won't fold for us.
export const fold = (text) =>
  String(text === undefined || text === null ? "" : text)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .toLowerCase();

// Anything that isn't a letter or a digit is a gap between words. Done after
// folding, so there are no accented letters left to lose.
export const tokenise = (text) =>
  fold(text)
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

// Light stemming, so "compliance" finds "compliant" and "genehmigen" finds
// "Genehmigung". Not a linguist's stemmer and not meant to be: a handful of
// endings from the two languages this product is read in, stripped while
// something recognisable is left. Over-stemming costs a wider net, which the
// ranking then sorts out; under-stemming costs a reader their answer.
const ENDINGS = [
  // English, longest first.
  "abilities", "ability", "ations", "ation", "ances", "ance", "ences", "ence",
  "ments", "ment", "ings", "ing", "ions", "ion", "ives", "ive", "ants", "ant",
  "ents", "ent", "ables", "able", "ibles", "ible", "ness", "ers", "er", "est",
  "ies", "ied", "ly", "ed", "es",
  // German, longest first.
  "lichkeiten", "lichkeit", "ungen", "ung", "lich", "keit", "heit", "isch",
  "enden", "ende", "enen", "ern", "end", "en", "em",
  // The endings both languages share, last: they are the cheapest and the
  // most likely to take a word apart that didn't need it.
  "n", "s",
];

const MIN_STEM = 4;
const MIN_STEMMED = 5;

// Applied until nothing more comes off, so "humans" and "human" land in the
// same place rather than one ending a step short of the other.
const endingOf = (value) => ENDINGS.find((end) => value.endsWith(end) && value.length - end.length >= MIN_STEM);

export const stem = (word) => {
  let value = String(word || "");
  for (let pass = 0; pass < 3; pass += 1) {
    if (value.length < MIN_STEMMED) break;
    const ending = endingOf(value);
    if (!ending) break;
    value = value.slice(0, value.length - ending.length);
  }
  // A silent final "e" is spelling, not meaning: "procure", "procurement" and
  // "procuring" are one word to a reader looking for any of them.
  if (value.length > MIN_STEM && value.endsWith("e")) value = value.slice(0, -1);
  return value;
};

const stemsOf = (text) => tokenise(text).map(stem);

// The same words, but as the file writes them: an acronym stays an acronym
// and an umlaut keeps its dots, so a chip reads in the model's own hand.
const surfaces = (text) => String(text || "").match(/[\p{L}\p{N}]+/gu) || [];

// Words that carry no question. Common English and German only - a word a
// customer uses is never in here, and neither is a word about processes.
const STOP = new Set([
  "a", "an", "the", "and", "or", "but", "of", "in", "on", "at", "to", "for", "from", "by", "with",
  "as", "is", "are", "was", "were", "be", "been", "being", "it", "its", "this", "that", "these",
  "those", "there", "here", "any", "all", "some", "each", "every", "which", "what", "who", "whom",
  "whose", "when", "where", "why", "how", "do", "does", "did", "can", "could", "should", "would",
  "will", "shall", "may", "might", "must", "have", "has", "had", "show", "list", "find", "give",
  "me", "my", "we", "our", "you", "your", "they", "them", "their", "he", "she", "his", "her",
  "if", "then", "than", "so", "such", "up", "out", "into", "over", "under", "about", "after",
  "before", "during", "while", "also", "only", "just", "very", "more", "most", "much", "many",
  "one", "two", "both", "other", "others", "same", "own", "per", "via",
  // Command words: how a person asks, rather than what they are asking for.
  "highlight", "display", "mark", "tell", "us", "point", "please", "look",
  "zeige", "zeig", "zeigen", "finde", "finden", "liste", "markiere", "suche",
  "der", "die", "das", "den", "dem", "des", "ein", "eine", "einen", "einem", "einer", "eines",
  "und", "oder", "aber", "von", "vom", "zu", "zum", "zur", "im", "in", "auf", "an", "am", "bei",
  "mit", "fur", "aus", "nach", "uber", "unter", "ist", "sind", "war", "waren", "sein", "wird",
  "werden", "wurde", "hat", "haben", "hatte", "kann", "konnen", "soll", "sollen", "muss", "mussen",
  "welche", "welcher", "welches", "wer", "wo", "wann", "warum", "wieso", "weshalb", "wie", "was",
  "es", "sie", "er", "ich", "wir", "ihr", "dies", "diese", "dieser", "dieses", "alle", "jeder",
  "noch", "auch", "nur", "sehr", "mehr", "als", "dann", "wenn", "damit", "durch", "gibt",
]);

// The words that turn a term into its opposite. "steps with no documentation"
// is a question about what an element does *not* carry, and the terms after
// one of these are required to be absent rather than present.
const NEGATIONS = new Set([
  "no", "not", "non", "without", "never", "lacking", "missing", "lack", "lacks", "except",
  "ohne", "kein", "keine", "keinen", "keiner", "keines", "nicht", "nichts",
]);

// The words a question starts with. A reader typing one of these at the front
// of a sentence is asking, not searching - and saying so is the honest answer
// (rule 3), not a failure.
const ASKING = new Set([
  "which", "what", "where", "who", "whom", "whose", "when", "why", "how",
  "does", "do", "did", "is", "are", "was", "were", "can", "could", "should",
  "would", "will", "shall", "am",
  "welche", "welcher", "welches", "was", "wo", "wer", "wen", "wem", "wessen",
  "wann", "warum", "wieso", "weshalb", "wie", "gibt", "kann", "konnen", "ist",
  "sind", "soll", "sollte", "durfen", "muss",
]);

// How much a sentence has to be left with, once the words that only say
// *how* a person is asking have been taken off it, before it is a question
// rather than terms. "where are the approvals" is one word - that is a
// search. "where could this breach the act?" is two, and no amount of
// matching answers it.
const QUESTION_CONTENT = 2;
const UNPROMPTED_CONTENT = 4;

// Whether what was typed reads as a question rather than as terms. `content`
// is what `readQuery` was left with; passing it in keeps the two from
// disagreeing about which words carried anything.
export const readsAsQuestion = (query, content) => {
  const text = String(query || "").trim();
  if (!text) return false;
  const words = tokenise(text);
  if (!words.length) return false;
  const left = content === undefined ? contentCount(text) : content;
  if (ASKING.has(words[0])) return left >= QUESTION_CONTENT;
  return text.endsWith("?") && left >= UNPROMPTED_CONTENT;
};


// ── The notation's own vocabulary ────────────────────────────────────────
//
// One small table, and the only place anything here knows what a modelling
// notation calls things. It maps **a reader's word** to **a fragment of a
// type's own name**, and it is used to *widen* a question, never to narrow
// what can be asked: a term that matches a concept still matches the words in
// the file as well, and a kind that matches no concept is read exactly as it
// was before.
//
// Why this is knowledge of the notation and not of a customer: every fragment
// on the right is part of a type name written into the standard the file
// declares - a task a person performs, one a system performs, a point where a
// flow divides, something that happens. None of them is a term any customer
// chose, and a file from a tool that names its types differently simply
// matches nothing here and is read by its own words instead.
const CONCEPTS = [
  { key: "human", one: "human step", many: "Human steps", words: ["human", "person", "people", "manual", "user"], types: ["user", "manual"] },
  {
    key: "automated",
    one: "automated step",
    many: "Automated steps",
    words: ["automated", "automatic", "automation", "system", "machine", "service", "script", "rule"],
    types: ["service", "script", "businessrule"],
  },
  { key: "decision", one: "decision", many: "Decisions", words: ["decision", "decide", "choice", "branch", "gateway", "split"], types: ["gateway"] },
  { key: "start", one: "starting point", many: "Starting points", words: ["start", "begin", "beginning"], types: ["startevent"] },
  { key: "finish", one: "ending", many: "Endings", words: ["end", "ending", "finish"], types: ["endevent"] },
  { key: "event", one: "event", many: "Events", words: ["event", "trigger"], types: ["event"] },
  { key: "step", one: "step", many: "Steps", words: ["step", "task", "activity", "action"], types: ["task", "activity", "subprocess", "transaction"] },
];

const CONCEPT_BY_STEM = new Map();
CONCEPTS.forEach((concept) => {
  concept.words.forEach((word) => {
    const key = stem(fold(word));
    if (!CONCEPT_BY_STEM.has(key)) CONCEPT_BY_STEM.set(key, concept);
  });
});

const conceptFor = (word) => CONCEPT_BY_STEM.get(stem(fold(word))) || null;

/**
 * Which of the notation's own words a kind answers to, if any - so a chip and
 * a row can wear the same mark the drawing gives the shape. `null` for a kind
 * the table has never heard of, which is read by its own words instead.
 */
export const conceptOfKind = (kind) => {
  const key = kindKeyOf(kind);
  return (CONCEPTS.find((concept) => isConcept(concept, key)) || {}).key || null;
};

// A kind, as something to compare against a type-name fragment: its own
// words, run together and folded.
const kindKeyOf = (kind) => fold(kind).replace(/[^a-z0-9]/g, "");

const isConcept = (concept, kindKey) => Boolean(concept) && concept.types.some((type) => kindKey.includes(type));

// ── The model, as something to search ────────────────────────────────────

// What each part of an element is worth when a term lands in it. A name is
// what the element is; what somebody wrote under it is what it says.
const NAME_WEIGHT = 6;
const KIND_WEIGHT = 3;
const VALUE_WEIGHT = 2;
const CONCEPT_WEIGHT = 5;

// The two parts of an element that are not one of its own rows. They are
// labelled in English because they are this panel's words for them, not the
// file's - every other label in the index comes out of the model.
export const NAME_PLACE = "name";
export const KIND_PLACE = "kind";

const MAX_ELEMENTS = 2000;
const MAX_FIELD_TEXT = 4000;

// What a tool recorded about itself, rather than anything a reader would ask
// for: the keys it refers to things by, who saved the file and when. Matched
// against a row's own label (its last part, where a tool nested it), so no
// judgement is made about what the value means.
const BOOKKEEPING =
  /(^|[^a-z])(id|ids|guid|uuid|key|keys|ref|refs|code|hash|version|revision|created|creation|updated|modified|changed|author|creator|editor|modifier|timestamp|exporter|tool|namespace|schema|url|uri|link)([^a-z]|$)/i;

const isBookkeepingLabel = (label) => BOOKKEEPING.test(String(label || "").split("·").pop().trim());

// A value that is a token rather than a word: a key, an id, a date, a shout.
// None of them is something a person would type into this panel, so none of
// them is ever offered as a suggestion. (They stay searchable - a reader who
// knows the key can still type it.)
const isToken = (value) => {
  const text = String(value || "").trim();
  if (!text) return true;
  if (/^[@_]/.test(text)) return true;
  if (/^[0-9]/.test(text)) return true;
  if (/_/.test(text) && text === text.toUpperCase()) return true;
  if (/^[0-9a-f-]{8,}$/i.test(text)) return true;
  if (/\d{4}-\d{2}-\d{2}|\d{2}:\d{2}/.test(text)) return true;
  if (text.length > 3 && !/[a-z]/.test(text)) return true;
  return false;
};

// Something somebody wrote, as opposed to something a tool recorded: several
// words of it, with words in them. Only prose is mined for the terms a
// diagram suggests - an attribute key repeated on every element is not what
// the model is about.
const MIN_PROSE_WORDS = 5;

const isProse = (value) => surfaces(value).length >= MIN_PROSE_WORDS && /[a-z]/.test(String(value));

const makeField = (label, weight, text) => {
  const value = String(text || "").slice(0, MAX_FIELD_TEXT);
  const words = tokenise(value);
  return {
    label,
    weight,
    text: value,
    folded: fold(value).replace(/\s+/g, " ").trim(),
    tokens: new Set(words),
    stems: new Set(words.map(stem)),
  };
};

/**
 * The drawing, ready to be asked about.
 *
 * @param {Array<{id, name, kind, rows: Array<{label, value}>}>} entries one per
 *   element the reader can see, exactly as `elementProperties` reads them.
 */
export function buildIndex(entries) {
  const list = Array.isArray(entries) ? entries.slice(0, MAX_ELEMENTS) : [];
  const clean = (row) => ({ label: String(row?.label || "").trim(), value: String(row?.value || "").trim() });
  const rowsOf = (entry) => (Array.isArray(entry?.rows) ? entry.rows : []).map(clean).filter((row) => row.label && row.value);

  // The names the drawing itself uses, so a row that only repeats them can be
  // told from one that says something.
  const known = new Set(
    list.map((entry) => fold(String(entry?.name || "").trim())).filter((name) => name && name.length > 2)
  );
  const namesOnly = (value) => {
    const parts = String(value)
      .split(/,| and |\u2192|->/)
      .map((part) => fold(part.trim()))
      .filter(Boolean);
    return parts.length > 0 && parts.filter((part) => known.has(part)).length * 2 >= parts.length;
  };

  // Which of a model's labels are its wiring rather than its words. Two
  // things have to be true at once, and neither alone would do: the label's
  // values are often other elements' names, *and* they are nearly all
  // different -
  // which is what "this one's source, that one's target" looks like. A lane
  // names elements too but repeats itself, so it survives; a paragraph is all
  // different but names nothing, so it survives as well.
  //
  // It matters because without it an element matches a word its *neighbour*
  // says, and "AI tasks" answers with the steps on either side of one.
  const tally = new Map();
  list.forEach((entry) => {
    rowsOf(entry).forEach((row) => {
      const seen = tally.get(row.label) || { values: new Set(), carriers: 0, names: 0 };
      seen.values.add(fold(row.value));
      seen.carriers += 1;
      if (namesOnly(row.value)) seen.names += 1;
      tally.set(row.label, seen);
    });
  });
  const wiring = new Set();
  tally.forEach((seen, label) => {
    if (seen.names * 3 >= seen.carriers && seen.values.size * 2 > seen.carriers) wiring.add(label);
  });

  const elements = list
    .map((entry, position) => {
      const id = String(entry?.id || "");
      if (!id) return null;
      // An element the modeller never named is known by the id the tool gave
      // it, and an id is not a word: it is never searched and never shown.
      const given = String(entry?.name || "").trim();
      const name = given && given !== id ? given : "";
      const kind = String(entry?.kind || "").trim();
      const rows = rowsOf(entry).filter((row) => !wiring.has(row.label));
      const fields = [];
      if (name) fields.push(makeField(NAME_PLACE, NAME_WEIGHT, name));
      if (kind) fields.push(makeField(KIND_PLACE, KIND_WEIGHT, kind));
      rows.forEach((row) => fields.push(makeField(row.label, VALUE_WEIGHT, row.value)));
      // Row labels answer the other half of a question - what an element does
      // *not* carry. They are never a positive match: a reader searching for a
      // word wants elements that say it, not elements that have somewhere to
      // say it.
      const labelStems = new Set();
      rows.forEach((row) => stemsOf(row.label).forEach((value) => labelStems.add(value)));
      return {
        id,
        name,
        kind,
        kindKey: kindKeyOf(kind),
        rows,
        fields,
        labelStems,
        position,
        caption: name || kind || id,
      };
    })
    .filter(Boolean);
  // The one label this model groups itself by, if it has one: few values,
  // carried by many elements, and not a paragraph. It is what a result row
  // says beside an element's name ("in Procurement"), and what the results
  // are gathered under when there are too many to read as one list. The word
  // is the file's own.
  const grouping = groupingLabel(elements);
  if (grouping) {
    elements.forEach((element) => {
      const row = element.rows.find((entry) => entry.label === grouping);
      element.context = row ? row.value : "";
    });
  }

  return {
    elements,
    size: elements.length,
    named: elements.filter((element) => element.name).length,
    grouping: grouping || "",
  };
}

// Few distinct values, many carriers, nothing that reads as prose: that is a
// grouping in any file, whatever the tool that wrote it called it.
function groupingLabel(elements) {
  const byLabel = new Map();
  elements.forEach((element) => {
    element.rows.forEach((row) => {
      if (isBookkeepingLabel(row.label) || isToken(row.value) || isProse(row.value)) return;
      const entry = byLabel.get(row.label) || { values: new Set(), carriers: 0 };
      entry.values.add(fold(row.value));
      entry.carriers += 1;
      byLabel.set(row.label, entry);
    });
  });
  let best = null;
  byLabel.forEach((entry, label) => {
    if (entry.values.size < MIN_MATCHES || entry.values.size > MAX_VALUES_PER_LABEL) return;
    if (entry.values.size * VALUES_TO_CARRIERS > entry.carriers) return;
    if (!best || entry.carriers > best.carriers) best = { label, carriers: entry.carriers, values: entry.values.size };
  });
  return best ? best.label : "";
}

const EMPTY_INDEX = { elements: [], size: 0, named: 0, grouping: "" };

// ── Matching ─────────────────────────────────────────────────────────────

const EXACT = 2;
const PARTIAL = 1;
const MIN_PREFIX = 4;

const hitsField = (field, term) => {
  if (field.tokens.has(term.raw)) return EXACT;
  if (field.stems.has(term.stem)) return EXACT;
  if (term.stem.length >= MIN_PREFIX) {
    for (const value of field.stems) if (value.startsWith(term.stem)) return PARTIAL;
  }
  return 0;
};

const hitsElement = (element, term) => {
  if (isConcept(term.concept, element.kindKey)) return true;
  if (element.fields.some((field) => hitsField(field, term))) return true;
  return element.labelStems.has(term.stem);
};

// How much of what somebody wrote is quoted back beside a result. Enough to
// see why it matched, never enough to read instead of the diagram.
const SNIPPET_BEFORE = 45;
const SNIPPET_AFTER = 80;

const snippetOf = (field, term) => {
  const text = field.text || "";
  if (!text) return "";
  const folded = fold(text);
  let at = folded.indexOf(term.raw);
  if (at < 0) at = folded.indexOf(term.stem);
  if (at < 0) return "";
  const start = Math.max(0, at - SNIPPET_BEFORE);
  const end = Math.min(text.length, at + Math.max(term.raw.length, term.stem.length) + SNIPPET_AFTER);
  const cut = text.slice(start, end).trim();
  return `${start > 0 ? "\u2026" : ""}${cut}${end < text.length ? "\u2026" : ""}`;
};

const PHRASE_BONUS = 4;

// One element against the terms that must be present. `null` where nothing
// landed at all; otherwise what was hit, where, and how well. A term that
// names something the notation itself has a word for matches the kind as well
// as the words - widening the question, never narrowing it.
const scoreElement = (element, terms, phrases) => {
  const places = new Map();
  const byTerm = new Map();
  let quote = null;
  let score = 0;
  let matched = 0;
  terms.forEach((term) => {
    let best = null;
    if (isConcept(term.concept, element.kindKey)) best = { score: CONCEPT_WEIGHT, label: KIND_PLACE, concept: true };
    element.fields.forEach((field) => {
      const strength = hitsField(field, term);
      if (!strength) return;
      const value = field.weight + (strength === EXACT ? 1 : 0);
      if (!best || value > best.score) best = { score: value, label: field.label, concept: false, field };
    });
    if (!best) return;
    matched += 1;
    score += best.score;
    // Why it matched, in the file's own words. Taken from what somebody
    // *wrote*, even where the name is what scored - a row saying only "User
    // task" under a name is a list, not an answer.
    if (!quote) {
      const written = element.fields
        .filter((field) => field.label !== NAME_PLACE && field.label !== KIND_PLACE && hitsField(field, term))
        .sort((a, b) => b.text.length - a.text.length)[0];
      const found = written ? snippetOf(written, term) : "";
      if (found) quote = { label: written.label, text: found, term: term.raw };
    }
    byTerm.set(term.raw, best);
    places.set(best.label, (places.get(best.label) || 0) + best.score);
  });
  // A phrase in quotation marks is the reader asking for exactly that, so it
  // is required rather than rewarded.
  const quoted = phrases.filter((phrase) => element.fields.some((field) => field.folded.includes(phrase)));
  if (quoted.length !== phrases.length) return null;
  if (phrases.length) score += PHRASE_BONUS * phrases.length;
  if (!matched && !phrases.length) return null;
  return {
    matched,
    score,
    byTerm,
    quote,
    places: [...places.entries()].sort((a, b) => b[1] - a[1]).map(([label]) => label),
  };
};

const asTerm = (word) => ({ raw: word, stem: stem(word), concept: conceptFor(word) });

// How many words a question was left with once the asking was taken off it.
function contentCount(text) {
  const read = readQuery(text);
  return read.terms.length + read.negatives.length + read.phrases.length;
}

/**
 * What was typed, as something to look for: the words that must be there, the
 * words that must not, and any phrase asked for exactly.
 */
export function readQuery(query) {
  const text = String(query || "");
  const phrases = [];
  const rest = text.replace(/[""]([^""]+)[""]|"([^"]+)"/g, (whole, one, two) => {
    const phrase = tokenise(one || two).join(" ");
    if (phrase) phrases.push(phrase);
    return " ";
  });
  const words = tokenise(rest);
  const positive = [];
  const negative = [];
  let negating = false;
  words.forEach((word) => {
    if (NEGATIONS.has(word)) {
      negating = true;
      return;
    }
    if (STOP.has(word)) return;
    (negating ? negative : positive).push(word);
  });
  return { terms: positive.map(asTerm), negatives: negative.map(asTerm), phrases };
}

// ── Saying what happened ─────────────────────────────────────────────────

const quote = (word) => `“${word}”`;

const listWords = (values) => {
  if (values.length <= 1) return values.join("");
  return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
};

const plural = (word) => {
  const value = String(word || "").trim();
  if (!value) return "elements";
  if (/(s|x|z|ch|sh)$/i.test(value)) return `${value}es`;
  if (/[^aeiou]y$/i.test(value)) return `${value.slice(0, -1)}ies`;
  return `${value}s`;
};

// What to call the things that matched. Where the question named something
// the notation has a word for, that word; where they are all of one kind, the
// file's own word for that kind; otherwise the plainest English there is.
const nounFor = (results, concept) => {
  const many = results.length !== 1;
  if (concept) return many ? concept.many.toLowerCase() : concept.one;
  const kinds = new Set(results.map((result) => result.kind).filter(Boolean));
  if (kinds.size === 1 && results.every((result) => result.kind)) {
    const kind = [...kinds][0].toLowerCase();
    return many ? plural(kind) : kind;
  }
  return many ? "elements" : "element";
};

const MAX_PLACES = 3;

// Which parts of the elements the terms landed in, commonest first. The words
// are the model's own labels, lower-cased - never a word written down here.
const placesIn = (results, words) => {
  const wanted = new Set(words.map((term) => term.raw));
  const weight = new Map();
  results.forEach((result) => {
    (result.byTerm ? [...result.byTerm.entries()] : []).forEach(([raw, hit]) => {
      if (!wanted.has(raw) || hit.concept) return;
      weight.set(hit.label, (weight.get(hit.label) || 0) + hit.score);
    });
  });
  return [...weight.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_PLACES)
    .map(([place]) => place.toLowerCase());
};

// The concept an answer can honestly be named after: one every single result
// answers to by its own kind. A question that widened to a concept *and* to
// the words ("AI tasks") usually has results the concept doesn't describe, and
// naming them after it would be the one thing this panel must not do.
const namingConcept = (model, terms, results) => {
  const fully = terms.filter(
    (term) => term.concept && results.length && results.every((result) => result.byTerm?.get(term.raw)?.concept)
  );
  if (!fully.length) return null;
  const size = (concept) => model.elements.filter((element) => isConcept(concept, element.kindKey)).length;
  // The most specific of them: the one the model has fewest of.
  return fully.map((term) => term.concept).sort((a, b) => size(a) - size(b))[0] || null;
};

// The answer, in words that say exactly what was matched and no more.
const describe = (model, results, terms, negatives, phrases, { partial }) => {
  const concept = namingConcept(model, terms, results);
  // A term the concept already accounts for is not said twice.
  const words = terms.filter((term) => !term.concept || term.concept !== concept);
  const named = words.filter((term) => !(term.concept && results.some((result) => result.byTerm?.get(term.raw)?.concept)));
  const asked = [...phrases.map((phrase) => quote(phrase)), ...words.map((term) => quote(term.raw))];
  const noun = nounFor(results, concept);
  // Where a term matched some results by what they *are* and others by what
  // they say, there is no one place to name - so none is named.
  const places = placesIn(results, named);
  // Several things a reader asked to be absent are all absent, so they read
  // as "or": "carrying no 'human' or 'loop'".
  const without = listWords(negatives.map((term) => quote(term.raw))).replace(/ and ([^ ]+)$/, " or $1");
  const missing = negatives.length ? ` and no ${without}` : "";
  let sentence;
  if (!asked.length) {
    sentence = `${results.length} ${noun}${negatives.length ? ` carrying no ${without}` : ""}.`;
  } else if (places.length === 1 && places[0] === NAME_PLACE) {
    sentence = `${results.length} ${noun} named after ${listWords(asked)}${missing}.`;
  } else if (places.length === 1) {
    sentence = `${results.length} ${noun} whose ${places[0]} mentions ${listWords(asked)}${missing}.`;
  } else if (places.length) {
    sentence = `${results.length} ${noun} matching ${listWords(asked)}${missing} \u2014 in their ${listWords(places)}.`;
  } else {
    sentence = `${results.length} ${noun} matching ${listWords(asked)}${missing}.`;
  }
  if (!partial) return sentence;
  return `${sentence} Nothing matches all of those, so these match at least one.`;
};

// What a reader who found nothing should try instead: the chip whose own
// words come closest to theirs, else the one that covers most of the model.
const nearestTo = (terms, chips) => {
  if (!chips.length) return null;
  const wanted = new Set(terms.map((term) => term.stem));
  let best = null;
  chips.forEach((chip) => {
    const shared = stemsOf(chip.label).filter((value) => wanted.has(value)).length;
    if (!best || shared > best.shared) best = { chip, shared };
  });
  return best && best.shared ? best.chip : chips[0];
};

const MAX_RESULTS = 300;

const asResult = (element, scored) => ({
  id: element.id,
  name: element.name,
  kind: element.kind,
  caption: element.caption,
  // Where it sits, in the file's own word for that, and how much somebody
  // wrote about it - what a row needs to say more than a name.
  context: element.context || "",
  written: element.rows.some((row) => isProse(row.value)),
  places: scored.places,
  byTerm: scored.byTerm,
  // Why it matched, quoted from the file: the words around the match.
  quote: scored.quote || null,
  score: scored.score,
});

/**
 * Ask the diagram.
 *
 * @returns {{
 *   mode: "idle" | "question" | "empty" | "matches",
 *   answer: string, note: string,
 *   results: Array<{id, name, kind, caption, places, score}>,
 *   ids: string[], nearest: object | null, question: boolean
 * }}
 */
export function ask(index, query, options = {}) {
  const model = index && Array.isArray(index.elements) ? index : EMPTY_INDEX;
  const chips = Array.isArray(options.suggestions) ? options.suggestions : [];
  const text = String(query || "").trim();
  if (!text) return { mode: "idle", answer: "", note: "", results: [], ids: [], nearest: null, question: false };

  const { terms, negatives, phrases } = readQuery(text);
  const question = readsAsQuestion(text, terms.length + negatives.length + phrases.length);
  // Nothing but words that carry no question: there is nothing to look for,
  // and dimming the whole drawing would say there was.
  if (!terms.length && !negatives.length && !phrases.length) {
    return {
      mode: "empty",
      question,
      answer: `There is nothing to look for in ${quote(text)}.`,
      note: "",
      results: [],
      ids: [],
      nearest: chips[0] || null,
    };
  }

  const allowed = model.elements.filter((element) => !negatives.some((term) => hitsElement(element, term)));
  const scored = allowed
    .map((element) => ({ element, scored: scoreElement(element, terms, phrases) }))
    .filter((row) => row.scored || (!terms.length && !phrases.length));
  const full = scored.filter((row) => !terms.length || row.scored.matched === terms.length);
  const partial = !full.length && terms.length > 1 && scored.length > 0;
  const kept = (partial ? scored : full)
    .map((row) => ({
      element: row.element,
      scored: row.scored || { matched: 0, score: 0, places: [], byTerm: new Map(), quote: null },
    }))
    .sort((a, b) => {
      if (b.scored.score !== a.scored.score) return b.scored.score - a.scored.score;
      // A named element says more than an unnamed one; after that, the order
      // the model itself is written in, so results read the way it reads.
      if (Boolean(b.element.name) !== Boolean(a.element.name)) return a.element.name ? -1 : 1;
      return a.element.position - b.element.position;
    })
    .slice(0, MAX_RESULTS);
  const results = kept.map((row) => asResult(row.element, row.scored));

  // A question is not a set of terms, and saying so is the answer (rule 3).
  // What its words happen to match is offered underneath, clearly as that.
  if (question) {
    return {
      mode: "question",
      question: true,
      answer:
        "That reads as a question rather than as words to look for. Answering it needs the assistant, and that isn't switched on here.",
      note: results.length
        ? `In the meantime: ${describe(model, results, terms, negatives, phrases, { partial })}`
        : "None of the words in it appear in this diagram either.",
      results,
      ids: results.map((result) => result.id),
      nearest: results.length ? null : nearestTo(terms, chips),
    };
  }

  if (!results.length) {
    const asked = [...phrases, ...terms.map((term) => term.raw)].map(quote);
    return {
      mode: "empty",
      question: false,
      answer: `Nothing in this diagram matches ${listWords(asked) || quote(text)}.`,
      note: "",
      results: [],
      ids: [],
      nearest: nearestTo(terms, chips),
    };
  }

  return {
    mode: "matches",
    question: false,
    answer: describe(model, results, terms, negatives, phrases, { partial }),
    note: "",
    results,
    ids: results.map((result) => result.id),
    nearest: null,
  };
}

// ── Suggestions, derived from this model ─────────────────────────────────
//
// Ordered the way a reader thinks rather than by arithmetic alone: what kinds
// of thing this diagram is made of, then the groups it divides them into,
// then the words that recur in what somebody wrote about them. Within each,
// the one that covers most of the model first.

const SOURCES = ["kind", "value", "term"];
const PER_SOURCE = { kind: 4, value: 3, term: 3 };
const MAX_CHIPS = 8;
const MIN_MATCHES = 2;
// A suggestion that picks out everything says nothing. Only applied where
// there is enough of a model for "almost all of it" to mean something.
const MOST_OF_IT = 0.8;
const ENOUGH_TO_JUDGE = 6;
const MAX_VALUES_PER_LABEL = 8;
const VALUES_TO_CARRIERS = 2;
const MAX_VALUE_LENGTH = 48;
const MAX_VALUE_WORDS = 6;
const MIN_TERM_LENGTH = 3;
const MAX_TERM_SHARE = 0.6;

const titled = (word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word);

// What the diagram is made of. The notation's own words first, where its
// types answer to one of them ("Human steps"), and any kind that answers to
// none of them under its own name ("Wibbles"), so a file from a tool nobody
// here has seen still says what it contains.
const kindCandidates = (model) => {
  const out = [];
  CONCEPTS.forEach((concept) => {
    const found = model.elements.filter((element) => isConcept(concept, element.kindKey));
    if (found.length < MIN_MATCHES || !found.some((element) => element.name)) return;
    out.push({
      source: "kind",
      label: concept.many,
      query: concept.many.toLowerCase(),
      hint: "",
      weight: found.length,
      concept: true,
      conceptKey: concept.key,
    });
  });
  const counts = new Map();
  model.elements.forEach((element) => {
    if (!element.kind) return;
    const entry = counts.get(element.kind) || { total: 0, named: 0 };
    entry.total += 1;
    if (element.name) entry.named += 1;
    counts.set(element.kind, entry);
  });
  counts.forEach((entry, kind) => {
    // A kind nothing is named in is a kind a reader has no use for: the
    // connectors of most files, which carry no words of their own.
    if (!entry.named || entry.total < MIN_MATCHES) return;
    out.push({ source: "kind", label: plural(kind), query: kind, hint: "", weight: entry.total, concept: false });
  });
  return out.sort((a, b) => b.weight - a.weight);
};

// A value the model repeats. A label carried by many elements with only a few
// distinct values is how any file says "these belong together" - a lane, a
// pool, an owner, a tool's own category. Which of those it is, is the file's
// business; that it groups the model is arithmetic. What a tool recorded
// about itself is left out: an id, a key, a timestamp and the person who
// saved the file are not something anybody asks a diagram for.
const valueCandidates = (model) => {
  const byLabel = new Map();
  model.elements.forEach((element) => {
    element.rows.forEach((row) => {
      if (isBookkeepingLabel(row.label) || isToken(row.value)) return;
      if (row.value.length > MAX_VALUE_LENGTH) return;
      if (tokenise(row.value).length > MAX_VALUE_WORDS) return;
      if (fold(row.value) === fold(element.name)) return;
      const entry = byLabel.get(row.label) || { values: new Map(), carriers: 0 };
      entry.values.set(row.value, (entry.values.get(row.value) || 0) + 1);
      entry.carriers += 1;
      byLabel.set(row.label, entry);
    });
  });
  const out = [];
  byLabel.forEach((entry, label) => {
    if (entry.values.size > MAX_VALUES_PER_LABEL) return;
    // A label that groups the model repeats each of its values; one that joins
    // elements up - this one's source, that one's target - has nearly as many
    // values as it has elements, and grouping by it would say nothing.
    if (entry.values.size * VALUES_TO_CARRIERS > entry.carriers) return;
    [...entry.values.entries()]
      .filter(([, count]) => count >= MIN_MATCHES)
      .sort((a, b) => b[1] - a[1])
      .forEach(([value, count]) => out.push({ source: "value", label: value, query: value, hint: label, weight: count }));
  });
  return out.sort((a, b) => b.weight - a.weight);
};

// The words that recur in what somebody actually wrote. Only prose is read -
// a paragraph, not an attribute key - so a tool's own vocabulary can never
// become a suggestion. Frequency over the elements carrying them, so a word
// used once is not a suggestion and a word used everywhere, which would
// select the whole model, is not one either.
const termCandidates = (model) => {
  const prose = model.elements.filter((element) =>
    element.rows.some((row) => !isBookkeepingLabel(row.label) && isProse(row.value))
  );
  if (!prose.length) return [];
  const byStem = new Map();
  prose.forEach((element) => {
    const seen = new Set();
    element.rows.forEach((row) => {
      if (isBookkeepingLabel(row.label) || !isProse(row.value)) return;
      // Read twice over: once folded, to decide which word this is, and once
      // as the file wrote it, so the chip carries the file's own spelling.
      surfaces(row.value).forEach((surface) => {
        const word = fold(surface);
        if (!word || STOP.has(word) || NEGATIONS.has(word)) return;
        if (word.length < MIN_TERM_LENGTH) return;
        if (/^[0-9]/.test(word)) return;
        const key = stem(word);
        if (seen.has(key)) return;
        seen.add(key);
        const entry = byStem.get(key) || { documents: 0, forms: new Map() };
        entry.documents += 1;
        entry.forms.set(surface, (entry.forms.get(surface) || 0) + 1);
        byStem.set(key, entry);
      });
    });
  });
  const ceiling = Math.max(MIN_MATCHES, Math.floor(prose.length * MAX_TERM_SHARE));
  return [...byStem.entries()]
    .filter(([, entry]) => entry.documents >= MIN_MATCHES && entry.documents <= ceiling)
    .sort((a, b) => b[1].documents - a[1].documents)
    .map(([, entry]) => {
      const form = [...entry.forms.entries()].sort((a, b) => b[1] - a[1])[0][0];
      const label = form === form.toLowerCase() ? titled(form) : form;
      return { source: "term", label, query: form, hint: "mentioned", weight: entry.documents };
    });
};

/**
 * Six to eight things worth asking this diagram, derived from this diagram.
 *
 * Each candidate is put through the same matching a reader's own words go
 * through, so the count a chip shows is the answer it gives - never a promise
 * made by one rule and kept by another.
 */
export function suggestions(index) {
  const model = index && Array.isArray(index.elements) ? index : EMPTY_INDEX;
  if (!model.elements.length) return [];
  const candidates = [...kindCandidates(model), ...valueCandidates(model), ...termCandidates(model)];
  const perSource = new Map();
  const seenSignature = new Set();
  const seenLabel = new Set();
  const covered = [];
  const chips = [];
  candidates.forEach((candidate) => {
    const used = perSource.get(candidate.source) || 0;
    if (used >= PER_SOURCE[candidate.source]) return;
    const label = String(candidate.label || "").trim();
    if (!label || seenLabel.has(fold(label))) return;
    const answer = ask(model, candidate.query);
    if (answer.results.length < MIN_MATCHES) return;
    // A suggestion is only worth offering where most of what it picks out is
    // something a reader can recognise, and where it leaves something out.
    const named = answer.results.filter((result) => result.name).length;
    if (named * 2 < answer.results.length) return;
    if (model.named >= ENOUGH_TO_JUDGE && named > model.named * MOST_OF_IT) return;
    const signature = answer.ids.slice().sort().join("|");
    if (seenSignature.has(signature)) return;
    // A kind the notation's own word already covers ("User tasks" under
    // "Human steps") is the same suggestion said twice.
    if (candidate.concept === false) {
      const inside = covered.some((taken) => answer.ids.every((id) => taken.has(id)));
      if (inside) return;
    }
    if (candidate.concept) covered.push(new Set(answer.ids));
    seenSignature.add(signature);
    seenLabel.add(fold(label));
    perSource.set(candidate.source, used + 1);
    chips.push({
      id: `${candidate.source}:${fold(label)}`,
      source: candidate.source,
      label,
      query: candidate.query,
      hint: candidate.hint,
      conceptKey: candidate.conceptKey || null,
      // What it picks out, so a chip can wear the colour of the things it
      // stands for rather than a colour chosen here.
      ids: answer.ids,
      count: answer.results.length,
    });
  });
  return chips
    .sort((a, b) => SOURCES.indexOf(a.source) - SOURCES.indexOf(b.source) || b.count - a.count)
    .slice(0, MAX_CHIPS);
}

/**
 * What this diagram is, in four figures - so the panel says something useful
 * before anybody asks it anything. Every word in the answer is the model's
 * own: the notation's word for a kind it contains, the label a tool grouped
 * it by, the label somebody wrote under.
 *
 * @returns {Array<{value: number, label: string}>}
 */
export function diagramFacts(index) {
  const model = index && Array.isArray(index.elements) ? index : EMPTY_INDEX;
  if (!model.elements.length) return [];
  const facts = [{ value: model.size, label: model.size === 1 ? "element" : "elements" }];

  // The two kinds it has most of, in the notation's own words.
  CONCEPTS.map((concept) => ({
    concept,
    found: model.elements.filter((element) => isConcept(concept, element.kindKey)).length,
  }))
    .filter((row) => row.found >= MIN_MATCHES && row.concept.key !== "step")
    .sort((a, b) => b.found - a.found)
    .slice(0, 2)
    .forEach((row) => facts.push({ value: row.found, label: row.concept.many.toLowerCase() }));

  // The grouping the model divides itself into, under the label it uses.
  if (model.grouping) {
    const values = new Set(model.elements.map((element) => fold(element.context || "")).filter(Boolean));
    if (values.size >= MIN_MATCHES) facts.push({ value: values.size, label: plural(model.grouping).toLowerCase() });
  }

  // How much of it somebody wrote about, under the label they wrote it under.
  const written = new Map();
  model.elements.forEach((element) => {
    const labels = new Set(
      element.rows.filter((row) => !isBookkeepingLabel(row.label) && isProse(row.value)).map((row) => row.label)
    );
    labels.forEach((label) => written.set(label, (written.get(label) || 0) + 1));
  });
  const most = [...written.entries()].sort((a, b) => b[1] - a[1])[0];
  if (most) facts.push({ value: most[1], label: `with ${most[0].toLowerCase()}` });

  return facts.slice(0, 4);
}

/**
 * One true sentence about the drawing, before anybody asks it anything.
 *
 * Not a count and not a boast: the two things a person reading a process
 * model notices first - where most of the work sits, and how much of it
 * nobody has written up. Both are arithmetic over this file, in this file's
 * own words, so a model nobody has seen gets its own observation.
 */
export function observation(index) {
  const model = index && Array.isArray(index.elements) ? index : EMPTY_INDEX;
  if (!model.elements.length) return "";
  const clauses = [];

  if (model.grouping) {
    const counts = new Map();
    model.elements.forEach((element) => {
      if (!element.context) return;
      counts.set(element.context, (counts.get(element.context) || 0) + 1);
    });
    const carried = [...counts.values()].reduce((sum, found) => sum + found, 0);
    const biggest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    if (biggest && carried) {
      const word = model.grouping.toLowerCase();
      clauses.push(
        biggest[1] * 2 >= carried
          ? `most of the work sits in ${biggest[0]}`
          : `${biggest[0]} carries more of it than any other ${word}`
      );
    }
  }

  const named = model.elements.filter((element) => element.name);
  const written = named.filter((element) => element.rows.some((row) => isProse(row.value))).length;
  if (named.length) {
    if (!written) clauses.push("nothing in it has been written up yet");
    else if (written === named.length) clauses.push("and everything named in it has something written about it");
    else clauses.push(`and ${named.length - written} of its ${named.length} named parts have nothing written about them`);
  }

  if (!clauses.length) return "";
  const sentence = clauses.join(", ").replace(/^and /, "");
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

export default ask;
